import { NavLink, Outlet } from "react-router-dom";
import { Palette, SlidersHorizontal, UsersRound } from "lucide-react";
import { useAuthStore } from "@/store/auth";
import { cn } from "@/lib/utils";

const personalSections = [
  {
    to: "/settings/general",
    icon: SlidersHorizontal,
    label: "General",
    description: "Studio behavior and defaults",
  },
  {
    to: "/settings/appearance",
    icon: Palette,
    label: "Appearance",
    description: "Theme, mode and colors",
  },
] as const;

export function SettingsPage() {
  const activeTeam = useAuthStore((state) => state.activeTeam);
  const canManageTeam = Boolean(
    activeTeam && ["team_admin", "super_admin"].includes(activeTeam.role)
  );
  const sections = canManageTeam
    ? [
        ...personalSections,
        {
          to: "/settings/team-access",
          icon: UsersRound,
          label: "Team access",
          description: "Members, invitations and roles",
        },
      ]
    : personalSections;

  return (
    <div className="page-container max-w-7xl sm:py-8">
      <header className="mb-6 sm:mb-8">
        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-studio-accent">
          Workspace control
        </p>
        <h1 className="mt-2 text-2xl font-semibold tracking-tight text-studio-ink">Settings</h1>
        <p className="mt-2 max-w-2xl text-sm leading-6 text-studio-muted">
          Tune your personal workspace or manage access for the active team.
        </p>
      </header>

      <div className="grid min-w-0 gap-5 lg:grid-cols-[15rem_minmax(0,1fr)] lg:gap-8">
        <aside className="min-w-0 lg:sticky lg:top-6 lg:self-start">
          <nav
            className={cn(
              "grid max-w-full gap-2 pb-1 lg:block lg:space-y-1 lg:pb-0",
              canManageTeam ? "grid-cols-3" : "grid-cols-2"
            )}
            aria-label="Settings sections"
          >
            {sections.map(({ to, icon: Icon, label, description }) => (
              <NavLink
                key={to}
                to={to}
                className={({ isActive }) =>
                  cn(
                    "group flex min-w-0 flex-col items-center gap-2 rounded-xl border px-2 py-3 text-center transition-all sm:flex-row sm:gap-3 sm:px-3 sm:text-left lg:items-center",
                    isActive
                      ? "border-studio-accent/25 bg-studio-accent-soft text-studio-accent shadow-sm"
                      : "border-transparent text-studio-muted hover:border-studio-border hover:bg-studio-card hover:text-studio-ink"
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    <span
                      className={cn(
                        "flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border transition-colors",
                        isActive
                          ? "border-studio-accent/20 bg-studio-panel text-studio-accent"
                          : "border-studio-border bg-studio-panel text-studio-muted group-hover:text-studio-ink"
                      )}
                    >
                      <Icon className="h-4 w-4" />
                    </span>
                    <span className="min-w-0 max-w-full">
                      <span className="block truncate text-xs font-semibold sm:text-sm">{label}</span>
                      <span className="mt-0.5 hidden truncate text-[11px] font-normal text-studio-muted lg:block">
                        {description}
                      </span>
                    </span>
                  </>
                )}
              </NavLink>
            ))}
          </nav>
        </aside>

        <section className="min-w-0">
          <Outlet />
        </section>
      </div>
    </div>
  );
}
